import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Experiences } from '../config/constants';
import { State } from '../store';
import { Experience } from '../types';

export type FlowVariation = 'Fb' | 'PayWhatYouWant' | 'Klima';

// TODO: Add the missing intro survey steps (/intro/role, etc.). Their absence causes some nav bugs.
export type Step =
  | '/'
  | '/fb'
  | '/pwyw'
  | '/intro/role'
  | '/calculator'
  | '/choose-plan'
  | '/klima'
  | '/checkout'
  | '/success';

// For reference, see react/src/hooks/userOnboardingFlow.test.ts
export const defaultFlow: Step[] = [
  '/',
  '/intro/role',
  '/calculator',
  '/choose-plan',
  '/checkout',
  '/success',
];

// The plan-selection page differs per flow variation. They are all the same
// logical step (the one right before /checkout); a user can end up physically
// on a variant that isn't in their computed flow — e.g. a Klima-imported user
// (flow === ['/klima', ...]) who goes through the standard /choose-plan page.
const planSteps: Step[] = ['/choose-plan', '/fb', '/pwyw', '/klima'];

const currentFlow = new Set<FlowVariation>();

export function addFlowVariation(flow: FlowVariation) {
  currentFlow.add(flow);
}

export function removeFlowVariation(flow: FlowVariation) {
  currentFlow.delete(flow);
}

export function clearFlow() {
  currentFlow.clear();
}

// Reconcile the variations this hook owns against current user state. Because
// `currentFlow` is a module-level singleton that survives across route changes
// and user switches (clearFlow is never called), we must both add AND remove —
// otherwise a stale variation (e.g. a Klima user's flow) leaks onto the next
// user in the same SPA session, or a Klima user keeps their flow after
// activating. `PayWhatYouWant` is managed by its own landing route, not here.
export function syncFlowVariations(user: State['user']) {
  const isUserFromFbAdsFlow = user.experiences.some(
    (experience: Experience) => experience.name === Experiences.FB_LANDING_PAGE
  );
  if (isUserFromFbAdsFlow) {
    addFlowVariation('Fb');
  } else {
    removeFlowVariation('Fb');
  }

  // `importedFromKlima` is a permanent flag, but the Klima migration flow only
  // applies until the user activates (completedOnboarding). After that they're
  // a regular user; keeping them on the Klima flow (which omits /choose-plan)
  // is what strands returning/churned Klima users on an off-flow plan page.
  if (user.importedFromKlima && !user.completedOnboarding) {
    addFlowVariation('Klima');
  } else {
    removeFlowVariation('Klima');
  }
}

export function getOnboardingSteps(
  flow: Set<FlowVariation> = currentFlow
): Step[] {
  const initialSteps = (() => {
    if (flow.has('Klima')) {
      return ['/klima'];
    }

    if (flow.has('Fb')) {
      return ['/fb'];
    }

    if (flow.has('PayWhatYouWant')) {
      return ['/pwyw'];
    }

    return ['/', '/intro/role', '/calculator', '/choose-plan'];
  })() as Step[];

  const steps: (Step | null)[] = [...initialSteps, '/checkout', '/success'];
  return steps.filter<Step>((step): step is Step => !!step);
}

// The return value doesn't necessarily indicate whether the user has visited the page, just whether they need to, assuming the step is in their flow
function isComplete(step: Step, state: State) {
  const { user } = state;

  switch (step) {
    case '/':
      return true;
    case '/fb':
    case '/pwyw':
    case '/choose-plan':
      return !!user.cart?.items.some((item) => item.sku === 'subscription');
    case '/intro/role':
      return (
        !!user.surveyAnswers ||
        user.experiences.find(
          (experience) => experience.name === Experiences.CARBON_REPORT
        )
      ); // We assume the carbon report directly follows the calculator, and the intro survey is effectively part of the calculator experience
    case '/calculator':
      return user.experiences.find(
        (experience) => experience.name === Experiences.CARBON_REPORT
      ); // We assume the carbon report directly follows the calculator
    default:
      // In all other cases, we haven't tracked whether the user needs to go there
      return undefined;
  }
}

function getCurrentStepIndex(
  currentStep = location.pathname,
  onboardingSteps: Step[]
) {
  const directIndex = onboardingSteps.findIndex((step) => {
    if (step === '/') {
      return step === currentStep;
    } else {
      return currentStep.startsWith(step);
    }
  });

  if (directIndex !== -1) {
    return directIndex;
  }

  // The current path may be a plan-selection page that isn't this flow's
  // variant (see `planSteps`). Resolve it to whichever plan step the flow does
  // contain so navigation advances to /checkout instead of throwing.
  const onPlanStep = planSteps.some((planStep) =>
    currentStep.startsWith(planStep)
  );
  if (onPlanStep) {
    return onboardingSteps.findIndex((step) => planSteps.includes(step));
  }

  return -1;
}

export function getNextStep(
  currentStep = location.pathname,
  onboardingSteps: Step[]
): Step {
  const currentIndex = getCurrentStepIndex(currentStep, onboardingSteps);
  if (currentIndex === -1) {
    throw new Error(`No step named ${currentStep}`);
  }

  const nextStep = onboardingSteps.find((step, index) => index > currentIndex);

  if (!nextStep) {
    throw new Error(`No more steps to continue to`);
  }

  return nextStep;
}

function getPreviousStep(
  currentStep = location.pathname,
  onboardingSteps: Step[]
): Step {
  return getNextStep(currentStep, [...onboardingSteps].reverse());
}

function getStepToContinue(steps: Step[], state: State) {
  const foundStep = steps.find((step) => !isComplete(step, state));

  if (!foundStep) {
    throw new Error(`No more steps to continue to`);
  }

  return foundStep;
}

export type OnboardingFlowOptions = { climateCamp?: boolean };

function addParams(step: Step, { climateCamp }: OnboardingFlowOptions): string {
  switch (step) {
    case '/calculator':
      return climateCamp ? `${step}?climate_camp=true` : step;
  }
  return step;
}

export function useOnboardingFlow(flow: Set<FlowVariation> = currentFlow) {
  const steps = getOnboardingSteps(flow);
  const state: State = useSelector((state: State) => state);

  if (flow === currentFlow) {
    useEffect(() => {
      syncFlowVariations(state.user);
    }, [
      state.user.experiences,
      state.user.importedFromKlima,
      state.user.completedOnboarding,
    ]);
  }

  return {
    isCurrentlyOnStep: (currentStep?: string) =>
      getCurrentStepIndex(currentStep, steps) !== -1,
    getOnboardingSteps: () => steps,
    getNextStep: (currentStep?: string, options?: OnboardingFlowOptions) =>
      addParams(getNextStep(currentStep, steps), options || {}),
    getPreviousStep: (currentStep?: string, options?: OnboardingFlowOptions) =>
      addParams(getPreviousStep(currentStep, steps), options || {}),
    getStepToContinue: (options?: OnboardingFlowOptions) =>
      addParams(getStepToContinue(steps, state), options || {}),
  };
}
